import axios from 'axios';

// Use relative URL in production so it works with any domain (Heroku URL or custom domain)
// In development, use localhost (v2)
const API_URL = process.env.REACT_APP_API_URL ||
                (window.location.hostname === 'localhost' ? 'http://localhost:5000/api' : '/api');

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors. A 401 from the login request itself is a failed
// sign-in (surface it to the form); a 401 anywhere else means the session
// expired, so send the user back to login with an explanation.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isLoginRequest = error.config?.url?.includes('/auth/login');
    const onLoginPage = window.location.pathname === '/login';
    if (error.response?.status === 401 && !isLoginRequest && !onLoginPage) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login?expired=1';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (userData) => api.post('/auth/register', userData),
  getProfile: () => api.get('/auth/me'),
  updateProfile: (data) => api.patch('/auth/profile', data),
  changePassword: (data) => api.post('/auth/change-password', data),
  logout: () => api.post('/auth/logout')
};

// Clients API
export const clientsAPI = {
  getAll: (params) => api.get('/clients', { params }),
  getOne: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.patch(`/clients/${id}`, data),
  assignStaff: (id, data) => api.post(`/clients/${id}/assign-staff`, data),
  unassignStaff: (id) => api.post(`/clients/${id}/unassign-staff`),
  draftAssignStaff: (id, data) => api.post(`/clients/${id}/draft-assign-staff`, data),
  draftUnassignStaff: (id) => api.post(`/clients/${id}/draft-unassign-staff`),
  confirmStaffAssignment: (id) => api.post(`/clients/${id}/confirm-staff-assignment`),
  resendStaffLink: (id, staffId) => api.post(`/clients/${id}/resend-staff-link`, { staffId }),
  updateMeetAndGreet: (id, data) => api.post(`/clients/${id}/meet-greet`, data),
  updateTrainingStatus: (id, data) => api.post(`/clients/${id}/training`, data),
  updateTrainingBatch: (id, data) => api.post(`/clients/${id}/training-batch`, data),
  updateSystemSetup: (id, data) => api.post(`/clients/${id}/system-setup`, data),
  lockSchedule: (id, data) => api.post(`/clients/${id}/lock-schedule`, data),
  startService: (id, data) => api.post(`/clients/${id}/start-service`, data),
  weekOneFollowup: (id, data) => api.post(`/clients/${id}/week-one-followup`, data),
  getOverdueTasks: (id) => api.get(`/clients/${id}/overdue-tasks`),
  addISPGoal: (id, goal) => api.post(`/clients/${id}/isp-goals`, { goal }),
  archive: (id, data) => api.post(`/clients/${id}/archive`, data || {}),
  unarchive: (id) => api.post(`/clients/${id}/unarchive`),
  getArchived: () => api.get('/clients/archived/list')
};

// Workflow API
export const workflowAPI = {
  getPipeline: () => api.get('/workflow/pipeline'),
  getUpcomingStarts: (days = 7) => api.get('/workflow/upcoming-starts', { params: { days } }),
  getOverdueTasks: () => api.get('/workflow/overdue-tasks'),
  getUpcomingTasks: () => api.get('/workflow/upcoming-tasks'),
  getStatistics: () => api.get('/workflow/statistics'),
  advanceStatus: (clientId) => api.post(`/workflow/${clientId}/advance`),
  revertStatus: (clientId, data) => api.post(`/workflow/${clientId}/revert`, data),
  resetToIntake: (clientId, data) => api.post(`/workflow/${clientId}/reset`, data),
  getRequirements: (clientId) => api.get(`/workflow/${clientId}/requirements`)
};

// Notifications API
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  getOne: (id) => api.get(`/notifications/${id}`),
  send: (data) => api.post('/notifications/send', data),
  resend: (id) => api.post(`/notifications/${id}/resend`),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.patch('/notifications/read-all/mark'),
  getStats: () => api.get('/notifications/stats/summary'),
  getEscalations: () => api.get('/notifications/escalations/pending')
};

// Reports API
export const reportsAPI = {
  getDashboard: () => api.get('/reports/dashboard'),
  getBadges: () => api.get('/reports/badges'),
  getCoverage: () => api.get('/reports/coverage'),
  getStaffUtilization: () => api.get('/reports/staff-utilization'),
  getTrainingCompliance: () => api.get('/reports/training-compliance'),
  getPacingPerformance: () => api.get('/reports/pacing-performance'),
  getWeekOneSatisfaction: () => api.get('/reports/week-one-satisfaction')
};

// Users API
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  getAvailableStaff: (params) => api.get('/users/available-staff', { params }),
  getOne: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.patch(`/users/${id}`, data),
  updateStaffProfile: (id, data) => api.patch(`/users/${id}/staff-profile`, data),
  updateTraining: (id, data) => api.post(`/users/${id}/training`, data),
  getExpiringTrainings: (id, daysAhead = 30) => api.get(`/users/${id}/expiring-trainings`, { params: { daysAhead } }),
  deactivate: (id) => api.delete(`/users/${id}`),
  createDSP: (data) => api.post('/users/dsp', data)
};

// Staff API (uses users endpoints)
export const staffAPI = {
  getAll: () => usersAPI.getAll({ role: 'DSP' }),
  getById: (id) => usersAPI.getOne(id),
  getAssignedClients: (id) => api.get(`/users/${id}/assigned-clients`),
  update: (id, data) => usersAPI.update(id, data),
  updateProfile: (id, data) => usersAPI.updateStaffProfile(id, data),
  addTraining: (id, training) => usersAPI.updateTraining(id, training)
};

export default api;